"use client";

import { useEffect, useMemo, useState } from "react";
import { DEFAULT_BRAND } from "@/enums/brands";
import { useBrandConfig } from "@/hooks/useBrandConfig";

interface BrandImageProps {
  brand?: string;
  className?: string;
  width?: number;
  height?: number;
  variant?: "small";
}

const defaultWidth = 115;
const defaultHeight = 46;
const defaultAspectRatio = defaultWidth / defaultHeight;

function calculateDimensions(
  aspectRatio: number,
  baseWidth: number,
  baseHeight: number,
  width?: number,
  height?: number
): { widthSet: number; heightSet: number } {
  if (width !== undefined && height === undefined) {
    return { widthSet: width, heightSet: Math.round(width / aspectRatio) };
  }
  if (height !== undefined && width === undefined) {
    return { widthSet: Math.round(height * aspectRatio), heightSet: height };
  }
  return {
    widthSet: width ?? baseWidth,
    heightSet: height ?? baseHeight,
  };
}

/* eslint-disable @next/next/no-img-element */

function resolveBrand(brand?: string): string {
  if (brand !== undefined) return brand;
  try {
    if (globalThis.window !== undefined) {
      return globalThis.localStorage?.getItem("brand") ?? DEFAULT_BRAND;
    }
  } catch {
    return DEFAULT_BRAND;
  }
  return DEFAULT_BRAND;
}

const BrandImage: React.FC<BrandImageProps> = ({ brand, className, width, height, variant }) => {
  const resolvedBrand = resolveBrand(brand);

  const { config, loading } = useBrandConfig(resolvedBrand);
  const [logoOverride, setLogoOverride] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const params = new URLSearchParams(globalThis.location?.search ?? "");
    const logoParam = params.get("logo");
    setLogoOverride(logoParam ? decodeURIComponent(logoParam) : null);
  }, []);

  const effectiveConfig = useMemo(() => {
    if (resolvedBrand === DEFAULT_BRAND) {
      if (variant === "small") {
        return {
          logoUrl: "/icons/gail2-icon-blue.svg",
          displayName: "Gail",
          aspectRatio: 1,
          defaultWidth: 40,
          defaultHeight: 40,
        };
      }
      return {
        logoUrl: "/icons/gail2-logo.svg",
        displayName: "Gail",
        aspectRatio: defaultAspectRatio,
        defaultWidth,
        defaultHeight,
      };
    }
    if (!config) return null;
    return {
      logoUrl: config.logoUrl,
      displayName: config.displayName,
      aspectRatio: defaultAspectRatio,
      defaultWidth,
      defaultHeight,
    };
  }, [resolvedBrand, variant, config]);

  if (!mounted) {
    const isSmall = variant === "small";
    const { widthSet, heightSet } = calculateDimensions(
      isSmall ? 1 : defaultAspectRatio,
      isSmall ? 40 : defaultWidth,
      isSmall ? 40 : defaultHeight,
      width,
      height
    );
    const fallbackUrl = isSmall ? "/icons/gail2-icon-blue.svg" : "/icons/gail2-logo.svg";
    return (
      <div
        className={className}
        style={{
          width: widthSet,
          height: heightSet,
          backgroundImage: `var(--brand-logo-url, url("${fallbackUrl}"))`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "left center",
        }}
        aria-hidden
      />
    );
  }

  if (logoOverride) {
    return (
      <img
        src={logoOverride}
        alt="Logo"
        style={{
          height: defaultHeight,
          maxWidth: "100px",
          objectFit: "contain",
        }}
      />
    );
  }

  if (resolvedBrand === "sebas" && config) {
    const sebasCombinedDefaultWidth = 159;
    const sebasDefaultHeight = 46;
    const sebasMascotDefaultWidth = 44;
    const sebasTextDefaultWidth = 115;
    const { heightSet } = calculateDimensions(
      sebasCombinedDefaultWidth / sebasDefaultHeight,
      sebasCombinedDefaultWidth,
      sebasDefaultHeight,
      width,
      height
    );
    const mascotWidth = Math.round(heightSet * (sebasMascotDefaultWidth / sebasDefaultHeight));
    const textWidth = Math.round(heightSet * (sebasTextDefaultWidth / sebasDefaultHeight));
    return (
      <div className={className ?? ""} style={{ height: `${heightSet}px` }}>
        <img
          src="/icons/SebasMascot.png"
          alt="Sebanda Insurance Mascot"
          width={mascotWidth}
          height={heightSet}
          className="mr-2"
        />
        <img
          src={config.logoUrl}
          alt={`${config.displayName} Logo`}
          width={textWidth}
          height={heightSet}
        />
      </div>
    );
  }

  if (effectiveConfig === null) {
    if (loading) {
      return (
        <div
          className={className}
          style={{
            width: width ?? defaultWidth,
            height: height ?? defaultHeight,
            backgroundColor: "var(--gail-color-primary, #eee)",
            borderRadius: 4,
          }}
          aria-hidden
        />
      );
    }
    return (
      <div
        className={className}
        style={{
          width: width ?? defaultWidth,
          height: height ?? defaultHeight,
          backgroundColor: "var(--gail-color-primary, #eee)",
          borderRadius: 4,
        }}
        aria-label="Logo"
      />
    );
  }

  const { widthSet, heightSet } = calculateDimensions(
    effectiveConfig.aspectRatio,
    effectiveConfig.defaultWidth,
    effectiveConfig.defaultHeight,
    width,
    height
  );

  return (
    <img
      src={effectiveConfig.logoUrl}
      alt={`${effectiveConfig.displayName} Logo`}
      width={widthSet}
      height={heightSet}
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
};

export default BrandImage;
